/**
 * StabilitySystem — balance/stability calculation for robots.
 *
 * Computes a stability score (0–1) based on:
 * - Center of mass (barycenter) of all components weighted by mass
 * - Support polygon formed by movement components (wheels, thrusters)
 * - How well the center of mass sits within/near the support
 *
 * === FLIGHT STABILITY (heading-relative) ===
 *
 * Flying robots use the DIRECTION component heading to define the flight axis.
 * All measurements are projected onto heading-relative axes:
 * - **Lateral axis** (perpendicular to heading) = wing-to-wing direction
 * - **Longitudinal axis** (along heading) = front-to-back direction
 *
 * Wing balance checks two things:
 * 1. Lateral moment balance (60%): equal lift left vs right → roll stability
 * 2. Longitudinal mirror symmetry (40%): left and right wings at same position
 *    along the flight axis → true mirror placement, not just weight balance
 *
 * Heading-aware penalties:
 * - Lateral CoM offset: critical (causes roll) → strong penalty
 * - Longitudinal CoM offset: tolerated (front-heavy planes are normal) → mild penalty
 * - Lateral mass asymmetry: critical → 0.4 penalty factor
 * - Wing asymmetry: catastrophic → 0.6 penalty factor
 *
 * === GROUND STABILITY ===
 *
 * Ground robots use the support polygon formed by wheels/thrusters.
 * - Multiple supports: CoM inside polygon = stable, outside = unstable
 * - Single support: unstable equilibrium, height/width ratio matters
 * - No supports: turrets use bottom row as base, others are very unstable
 * - Stab Legs: perfect stability (anchored)
 *
 * Effects applied by the Robot:
 * - Speed multiplied by stability (unbalanced = slower, friction from dragging)
 * - Weapon accuracy penalty (spread increases with instability)
 * - Visual tilt proportional to imbalance direction
 */

import { ComponentCategory, ComponentType, COMPONENT_DEFS } from '@/components/ComponentDefs';
import { GOD } from '@/config/GodMode';

export interface StabilityResult {
  /** 0 = completely unstable, 1 = perfectly balanced */
  score: number;
  /** Center of mass relative to robot center (0,0) */
  comX: number;
  comY: number;
  /** Tilt angle in radians (visual lean direction) */
  tiltAngle: number;
  /** Number of support points (wheels/thrusters) */
  supportCount: number;
  /** Human-readable label */
  label: string;
  /** Foundation quality factor (1.0 = neutral, <1 = penalty from base components) */
  foundationFactor: number;
  /** Component types causing the worst foundation penalties (for UI feedback) */
  foundationPenaltyTypes: ComponentType[];
}

// ── Foundation weight lookup ──
// Maps each ComponentType to a GOD weight parameter category.
// Movement components are skipped (they serve as support points instead).
function getFoundationWeight(type: ComponentType): number {
  switch (type) {
    case ComponentType.STRUCTURE:
    case ComponentType.ARMOR:
      return GOD.stabFoundationWeightStructure;
    case ComponentType.STRUCTURE_TRIANGLE:
      return GOD.stabFoundationWeightBracket;
    case ComponentType.BATTERY:
      return GOD.stabFoundationWeightBattery;
    case ComponentType.DIRECTION:
    case ComponentType.RECHARGE_MODULE:
      return GOD.stabFoundationWeightNeutral;
    case ComponentType.SENSOR_SHORT:
    case ComponentType.SENSOR_MEDIUM:
      return GOD.stabFoundationWeightSensor;
    case ComponentType.REPAIR_MODULE:
      return GOD.stabFoundationWeightRepair;
    case ComponentType.MINE_LAYER:
    case ComponentType.EXCAVATOR:
    case ComponentType.CAMOUFLAGE:
      return GOD.stabFoundationWeightUtility;
    // All weapon types
    case ComponentType.CANNON_LIGHT:
    case ComponentType.CANNON_HEAVY:
    case ComponentType.BURST_CANNON:
    case ComponentType.MISSILE_SAM:
    case ComponentType.MISSILE_SOL_SOL:
    case ComponentType.FLAMETHROWER:
    case ComponentType.LASER:
    case ComponentType.ROCKET_POD:
    case ComponentType.EXPLOSIVE_CHARGE:
      return GOD.stabFoundationWeightWeapon;
    // Movement components are not evaluated (they are support points)
    case ComponentType.WHEEL:
    case ComponentType.THRUSTER:
    case ComponentType.WING:
    case ComponentType.TURBINE:
    case ComponentType.JUMPER:
    case ComponentType.STAB_LEGS:
      return 1.0; // neutral — these are filtered out anyway
    default:
      return GOD.stabFoundationWeightNeutral;
  }
}

/**
 * Compute the foundation quality factor from the bottom row components.
 * Returns a mass-weighted average of per-type foundation weights.
 * Movement components in the bottom row are excluded (they serve as support points).
 */
function computeFoundationFactor(bottomRow: CompEntry[]): { factor: number; worstTypes: ComponentType[] } {
  // Filter out movement components — they are support points, not foundation
  const foundationComps = bottomRow.filter(c => {
    const def = COMPONENT_DEFS[c.type];
    return def.category !== ComponentCategory.MOVEMENT && c.type !== ComponentType.STAB_LEGS;
  });

  if (foundationComps.length === 0) {
    return { factor: 1.0, worstTypes: [] };
  }

  let totalMass = 0;
  let weightedSum = 0;
  const worstTypes: ComponentType[] = [];

  for (const c of foundationComps) {
    const def = COMPONENT_DEFS[c.type];
    const w = getFoundationWeight(c.type);
    totalMass += def.mass;
    weightedSum += w * def.mass;
    if (w < 0.9 && !worstTypes.includes(c.type)) {
      worstTypes.push(c.type);
    }
  }

  const rawFactor = totalMass > 0 ? weightedSum / totalMass : 1.0;
  const factor = Math.max(GOD.stabFoundationMin, Math.min(GOD.stabFoundationMax, rawFactor));

  // Sort worst types by weight (worst first)
  worstTypes.sort((a, b) => getFoundationWeight(a) - getFoundationWeight(b));

  return { factor, worstTypes };
}

export interface CompEntry {
  type: ComponentType;
  lx: number;
  ly: number;
  broken?: boolean; // true = dead weight, still contributes to mass but not to function
}

/**
 * Calculate the stability of a robot design.
 * @param components Array of components with their local positions
 * @param designedWithMovement True if robot originally had movement components (wheels/thrusters)
 * @param buildHeading Optional heading from DIRECTION component (radians). Used for flying robots
 *        to distinguish lateral (wing) asymmetry from longitudinal (front/back) asymmetry.
 * @returns StabilityResult with score, center of mass, and tilt
 */
export function calculateStability(components: CompEntry[], designedWithMovement = false, buildHeading?: number): StabilityResult {
  if (components.length === 0) {
    return { score: 0, comX: 0, comY: 0, tiltAngle: 0, supportCount: 0, label: 'No components', foundationFactor: 1.0, foundationPenaltyTypes: [] };
  }

  // ── 1. Compute center of mass ──
  // Broken components are dead weight: they still have mass but reduced (dangling wreckage).
  // This means losing components on one side shifts the effective CoM.
  const BROKEN_MASS_FACTOR = 0.5; // broken parts retain 50% mass as dead weight
  let totalMass = 0;
  let wmX = 0;
  let wmY = 0;
  for (const c of components) {
    const def = COMPONENT_DEFS[c.type];
    const effectiveMass = c.broken ? def.mass * BROKEN_MASS_FACTOR : def.mass;
    totalMass += effectiveMass;
    wmX += c.lx * effectiveMass;
    wmY += c.ly * effectiveMass;
  }
  const comX = totalMass > 0 ? wmX / totalMass : 0;
  const comY = totalMass > 0 ? wmY / totalMass : 0;

  // ── 2. Identify support points (only working components — broken ones are dead weight) ──
  const working = components.filter(c => !c.broken);
  const supports: { x: number; y: number }[] = [];
  const maxCompY = components.length > 0 ? Math.max(...components.map(c => c.ly)) : 0;
  for (const c of working) {
    const def = COMPONENT_DEFS[c.type];
    if (def.category === ComponentCategory.MOVEMENT) {
      supports.push({ x: c.lx, y: c.ly });
    }
    // Bottom jumpers count as support points (they're springs touching the ground)
    if (c.type === ComponentType.JUMPER && c.ly >= maxCompY - 1) {
      supports.push({ x: c.lx, y: c.ly });
    }
  }

  // ── 3. Calculate stability score ──
  let score: number;
  let label: string;

  // Stab Legs grant perfect stability (anchored to ground) — only if not broken
  const hasStabLegs = working.some(c => c.type === ComponentType.STAB_LEGS);

  // ── Flying robots: stability depends on CoM alignment with heading + wing presence ──
  // Only working (non-broken) wings/turbines provide lift and stabilization
  const wings = working.filter(c => c.type === ComponentType.WING);
  const turbines = working.filter(c => c.type === ComponentType.TURBINE);
  const hasFlightComp = wings.length > 0 || turbines.length > 0;

  if (hasFlightComp && !hasStabLegs) {
    // ── Flight stability ──
    // The DIRECTION component heading defines the flight axis.
    // Only one thing matters for wings: are they symmetric on each side of that axis?
    //
    //        A                    ^
    //   ─────────── D→      ─────|───── D
    //        A                   |
    //                            ▼
    //
    // Wings on opposite sides of the heading axis = stable.
    // Wings only on one side or missing = catastrophic.

    const heading = buildHeading ?? Math.PI; // default: facing left (Phaser convention)
    const fwdX = Math.cos(heading);
    const fwdY = Math.sin(heading);
    // Lateral axis: perpendicular to heading (wing-to-wing direction)
    const latX = -fwdY;
    const latY = fwdX;

    // ── 1. Wing symmetry: are wings mirrored on each side of the flight axis? ──
    // Two checks:
    //   a) Lateral moment balance: equal lift left vs right (roll stability)
    //   b) Longitudinal alignment: left-side and right-side wings at same height
    //      along the flight axis (true mirror symmetry, not just weight balance)
    const hasWings = wings.length > 0;
    let wingBalance = 1.0;

    if (hasWings) {
      let momentLeft = 0, momentRight = 0;
      // Track longitudinal positions of left vs right wings for symmetry check
      let lonSumLeft = 0, countLeft = 0;
      let lonSumRight = 0, countRight = 0;

      for (const w of wings) {
        const m = COMPONENT_DEFS[w.type].mass;
        const lat = w.lx * latX + w.ly * latY; // signed lateral distance
        const lon = w.lx * fwdX + w.ly * fwdY; // longitudinal position
        if (lat < -1) {
          momentLeft += (Math.abs(lat) + 5) * m;
          lonSumLeft += lon; countLeft++;
        } else if (lat > 1) {
          momentRight += (Math.abs(lat) + 5) * m;
          lonSumRight += lon; countRight++;
        } else {
          momentLeft += 5 * m * 0.5; momentRight += 5 * m * 0.5;
        }
      }
      const total = momentLeft + momentRight;
      const lateralBalance = total > 0 ? 1 - Math.abs(momentLeft - momentRight) / total : 1;

      // Longitudinal symmetry: are left and right wings at the same "height" along axis?
      // avg lon left vs avg lon right — difference = asymmetric placement
      let lonSymmetry = 1.0;
      if (countLeft > 0 && countRight > 0) {
        const avgLonLeft = lonSumLeft / countLeft;
        const avgLonRight = lonSumRight / countRight;
        const lonDiff = Math.abs(avgLonLeft - avgLonRight);
        // 20px offset (1 grid cell) = significant asymmetry
        lonSymmetry = Math.max(0, 1 - lonDiff / 30);
      } else if (countLeft === 0 || countRight === 0) {
        // All wings on one side = no symmetry at all
        lonSymmetry = 0;
      }

      wingBalance = lateralBalance * 0.6 + lonSymmetry * 0.4;
    }

    // ── 2. Wing spread: wider wings = more inherent stability ──
    let wingSpread = 0;
    for (const w of wings) {
      const latDist = Math.abs(w.lx * latX + w.ly * latY);
      wingSpread += (latDist + 5) * COMPONENT_DEFS[w.type].mass * 0.003;
    }
    wingSpread *= wingBalance; // spread only helps when balanced
    wingSpread += turbines.length * 0.04; // turbines add gyroscopic stability
    wingSpread = Math.min(0.85, wingSpread);

    // ── 3. CoM offset: lateral is critical, longitudinal is tolerated ──
    const comLateral = Math.abs(comX * latX + comY * latY);
    const comLongitudinal = Math.abs(comX * fwdX + comY * fwdY);
    const comPenalty = Math.min(0.6, comLateral / 15 + comLongitudinal / 60);

    // ── 4. Mass distribution: lateral asymmetry = roll, longitudinal = normal ──
    let massLeft = 0, massRight = 0;
    for (const c of components) {
      const def = COMPONENT_DEFS[c.type];
      const m = c.broken ? def.mass * BROKEN_MASS_FACTOR : def.mass;
      const lat = c.lx * latX + c.ly * latY;
      if (lat < -1) massLeft += m;
      else if (lat > 1) massRight += m;
    }
    const totalMassLat = massLeft + massRight;
    const balLateral = totalMassLat > 0 ? 1 - Math.abs(massLeft - massRight) / totalMassLat : 1;
    const massAsymmetryPenalty = (1 - balLateral) * 0.4;

    // ── 5. Penalties ──
    const wingAsymmetryPenalty = hasWings ? (1 - wingBalance) * 0.6 : 0;
    const effectiveComPenalty = comPenalty * (1 - wingSpread);

    score = Math.max(0.1, 1 - effectiveComPenalty - wingAsymmetryPenalty - massAsymmetryPenalty);

    if (score > 0.85) label = 'Aerodynamic';
    else if (score > 0.6) label = 'Stable flight';
    else if (score > 0.4) label = 'Drifting';
    else label = 'Unstable flight';

    const tiltAngle = (comLateral > 1 ? Math.atan2(comY, comX) * 0.15 * (1 - wingSpread) : 0)
      + (hasWings && wingBalance < 0.9 ? (1 - wingBalance) * 0.4 : 0);

    return { score, comX, comY, tiltAngle, supportCount: supports.length, label, foundationFactor: 1.0, foundationPenaltyTypes: [] };
  }

  // ── Detect bottom row (shared for ground stability + tilt) ──
  const SPACING = 20;
  const maxY = Math.max(...components.map(c => c.ly));
  const bottomRow = components.filter(c => Math.abs(c.ly - maxY) < SPACING * 0.5);

  // ── Foundation factor: penalize non-structural components at the base ──
  let foundationFactor = 1.0;
  let foundationPenaltyTypes: ComponentType[] = [];
  if (GOD.stabFoundationEnabled && bottomRow.length > 0) {
    const ff = computeFoundationFactor(bottomRow);
    foundationFactor = ff.factor;
    foundationPenaltyTypes = ff.worstTypes;
  }

  if (hasStabLegs) {
    score = 1.0;
    label = 'Anchored';
    foundationFactor = 1.0;
    foundationPenaltyTypes = [];
  } else if (supports.length === 0) {
    // No working wheels — use bottommost component row as ground contact.
    // A turret designed without wheels uses its bottom as a stable base.
    // A robot that lost all its wheels is much less stable.

    if (bottomRow.length === 0) {
      score = 0;
      label = 'No support';
    } else if (!designedWithMovement) {
      // Fixed turret: designed without wheels — the entire bottom row is a flat base.
      // Inherently stable (bolted to the ground), only penalized for extreme CoM offset.
      const groundSupports = bottomRow.map(c => ({ x: c.lx, y: c.ly }));
      score = groundSupports.length >= 2
        ? computePolygonStability(comX, comY, groundSupports)
        : 0.7;
      // Apply foundation factor: structural base = bonus, weapon base = penalty
      score *= foundationFactor;
      // Fixed turrets get a stability floor (scaled by foundation factor)
      score = Math.max(0.65 * foundationFactor, score);
      label = score > 0.85 ? 'Fixed turret' : 'Stable turret';
    } else {
      // Robot that lost all its wheels — very unstable
      const groundSupports = bottomRow.map(c => ({ x: c.lx, y: c.ly }));
      if (groundSupports.length === 1) {
        const sx = groundSupports[0].x;
        const sy = groundSupports[0].y;
        const dist = Math.sqrt((comX - sx) ** 2 + (comY - sy) ** 2);
        score = Math.max(0.15, 1 - dist / 20) * 0.5;
        const verticalSpan = Math.max(...components.map(c => c.ly)) - Math.min(...components.map(c => c.ly));
        const horizontalSpan = Math.max(...components.map(c => c.lx)) - Math.min(...components.map(c => c.lx));
        const heightSlots = Math.round(verticalSpan / SPACING);
        const widthSlots = Math.round(horizontalSpan / SPACING);
        const columnPenalty = widthSlots > 0
          ? Math.min(1, widthSlots / Math.max(1, heightSlots))
          : (heightSlots > 0 ? 0.2 : 1);
        score *= 0.3 + 0.7 * columnPenalty;
      } else {
        score = computePolygonStability(comX, comY, groundSupports);
      }
      score *= 0.5 * foundationFactor;
      label = score < 0.2 ? 'Collapsing' : 'No wheels';
    }
  } else if (supports.length === 1) {
    // Single support: unstable equilibrium — like balancing a pencil on its tip.
    // The taller the structure above the wheel, the more unstable.
    const sx = supports[0].x;
    const sy = supports[0].y;
    const dist = Math.sqrt((comX - sx) ** 2 + (comY - sy) ** 2);
    const maxDist = 20;
    score = Math.max(0.15, 1 - dist / maxDist);
    score *= 0.5;

    // Height penalty: count how many components are stacked above/beside the wheel
    const verticalSpan = Math.max(...components.map(c => c.ly)) - Math.min(...components.map(c => c.ly));
    const horizontalSpan = Math.max(...components.map(c => c.lx)) - Math.min(...components.map(c => c.lx));
    const heightSlots = Math.round(verticalSpan / SPACING);
    const widthSlots = Math.round(horizontalSpan / SPACING);
    const columnPenalty = widthSlots > 0
      ? Math.min(1, widthSlots / Math.max(1, heightSlots))
      : (heightSlots > 0 ? 0.2 : 1);
    score *= 0.3 + 0.7 * columnPenalty;

    // Attenuated foundation penalty for mobile robots
    const mobileFF = 1.0 + (foundationFactor - 1.0) * GOD.stabFoundationMobileInfluence;
    score *= mobileFF;

    label = score < 0.2 ? 'Will topple' : score < 0.3 ? 'Very unstable' : 'Unstable';
  } else {
    // Multiple supports: check if CoM falls within the support polygon
    score = computePolygonStability(comX, comY, supports);

    // Attenuated foundation penalty for mobile robots
    const mobileFF = 1.0 + (foundationFactor - 1.0) * GOD.stabFoundationMobileInfluence;
    score *= mobileFF;

    if (score > 0.85) label = 'Very stable';
    else if (score > 0.6) label = 'Stable';
    else if (score > 0.35) label = 'Unbalanced';
    else label = 'Unstable';
  }

  score = Math.min(1, Math.max(0, score));

  // ── 4. Compute visual tilt angle ──
  // The robot tilts TOWARD its CoM: if CoM is to the right of the support
  // centroid, gravity pulls it right → positive screen rotation.
  // Only the horizontal (X) offset produces visible tilt in side-view.
  let tiltAngle = 0;

  // Effective supports for tilt: wheels if available, otherwise bottom row
  let tiltSupports: { x: number; y: number }[] = supports;
  if (supports.length === 0 && bottomRow.length > 0) {
    tiltSupports = bottomRow.map(c => ({ x: c.lx, y: c.ly }));
  }

  if (tiltSupports.length > 0) {
    const scx = tiltSupports.reduce((s, p) => s + p.x, 0) / tiltSupports.length;
    const dx = comX - scx; // positive = CoM is to the right of supports
    // Single support → higher max tilt so gravity alone can topple the robot
    const maxTilt = tiltSupports.length === 1 ? 0.6 : 0.26; // ~34° vs ~15°
    if (Math.abs(dx) > 0.5) {
      // Tilt proportional to horizontal CoM offset and instability
      tiltAngle = Math.sign(dx) * Math.min(1, Math.abs(dx) / 30) * maxTilt * (1 - score);
    } else if (tiltSupports.length === 1 && score < 0.35) {
      // Unstable equilibrium: even with centered CoM, a single-wheel robot
      // will lean to one side — like a pencil balanced on its tip.
      const massLeft = components.filter(c => c.lx < scx).reduce((s, c) => s + COMPONENT_DEFS[c.type].mass, 0);
      const massRight = components.filter(c => c.lx > scx).reduce((s, c) => s + COMPONENT_DEFS[c.type].mass, 0);
      const leanDir = massRight >= massLeft ? 1 : -1;
      tiltAngle = leanDir * maxTilt * (0.3 + 0.7 * (1 - score / 0.35));
    }
  }

  return { score, comX, comY, tiltAngle, supportCount: tiltSupports.length, label, foundationFactor, foundationPenaltyTypes };
}

/**
 * Compute stability score for CoM relative to a set of support points.
 * Uses axis-specific extents so collinear supports (e.g. a row of wheels)
 * still provide proper stability in the perpendicular direction.
 * Score = 1 when CoM is at centroid, decreasing as it moves toward/beyond edges.
 */
function computePolygonStability(
  cx: number, cy: number,
  supports: { x: number; y: number }[],
): number {
  // Compute centroid of supports
  const scx = supports.reduce((s, p) => s + p.x, 0) / supports.length;
  const scy = supports.reduce((s, p) => s + p.y, 0) / supports.length;

  // Compute support extents per axis
  let maxDx = 0;
  let maxDy = 0;
  for (const p of supports) {
    maxDx = Math.max(maxDx, Math.abs(p.x - scx));
    maxDy = Math.max(maxDy, Math.abs(p.y - scy));
  }

  // Special case: all supports stacked on same position
  if (maxDx < 1 && maxDy < 1) {
    const dist = Math.sqrt((cx - scx) ** 2 + (cy - scy) ** 2);
    return Math.max(0.15, 1 - dist / 20) * 0.6;
  }

  // ── Collinear supports: row of wheels on same line ──
  // When supports have no spread on one axis, stability on that axis depends
  // on whether the CoM is on the "supported side" (above a row of wheels = normal).
  // A tall robot on a wide wheelbase IS stable — the weight above wheels is expected.
  // Only the perpendicular (spread) axis uses the normal ratio check.
  // Height above wheels applies a mild penalty (higher CoG = easier to tip laterally).

  const collinearY = maxDy < 1 && maxDx >= 1; // horizontal row of wheels
  const collinearX = maxDx < 1 && maxDy >= 1; // vertical column of wheels

  if (collinearY) {
    // Row of wheels: stability = how centered the CoM is over the wheelbase (X axis)
    const ratioX = Math.abs(cx - scx) / maxDx;
    // Height penalty: taller robots are slightly less stable laterally
    // Distance from CoM to wheel line — moderate penalty (max -25% for very tall)
    const heightAbove = Math.abs(cy - scy);
    const heightPenalty = Math.max(0.75, 1 - heightAbove / 200);
    if (ratioX <= 1) {
      return (1.0 - ratioX * 0.5) * heightPenalty;
    } else {
      return Math.max(0, (0.5 - (ratioX - 1) * 0.8)) * heightPenalty;
    }
  }

  if (collinearX) {
    // Vertical column of wheels: stability = how centered on Y axis
    const ratioY = Math.abs(cy - scy) / maxDy;
    const widthOffset = Math.abs(cx - scx);
    const widthPenalty = Math.max(0.75, 1 - widthOffset / 200);
    if (ratioY <= 1) {
      return (1.0 - ratioY * 0.5) * widthPenalty;
    } else {
      return Math.max(0, (0.5 - (ratioY - 1) * 0.8)) * widthPenalty;
    }
  }

  // ── Non-collinear: 2D support polygon ──
  // Elliptical ratio: check each axis independently then combine
  const ratioX = Math.abs(cx - scx) / maxDx;
  const ratioY = Math.abs(cy - scy) / maxDy;
  const ratio = Math.sqrt(ratioX ** 2 + ratioY ** 2);

  // Inside ellipse (ratio < 1): good stability, centered = best
  // Outside ellipse (ratio > 1): rapidly decreasing stability
  if (ratio <= 1) {
    return 1.0 - ratio * 0.5;
  } else {
    return Math.max(0, 0.5 - (ratio - 1) * 0.8);
  }
}

/**
 * Compute speed multiplier from stability.
 * Stable = full speed, unstable = slower (friction, dragging).
 */
export function stabilitySpeedMultiplier(stability: number): number {
  // 1.0 at stability=1, 0.3 at stability=0 (never fully immobile)
  return 0.3 + 0.7 * stability;
}

/**
 * Compute accuracy multiplier from stability.
 * Returns a spread factor: 0 = perfect accuracy, higher = more spread.
 * Used to offset projectile target position.
 */
export function stabilityAccuracySpread(stability: number): number {
  // Stable robots (≥0.5) have perfect accuracy — only unstable ones lose precision
  if (stability >= 0.5) return 0;
  // Below 0.5: linear ramp from 0 to 20px spread
  return (0.5 - stability) * 40;
}
